import { Anchor, Button, Group, Select, SimpleGrid, Stack, Text, TextInput } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { rawApi } from '../../api/client';
import { fieldErrors } from '../../api/errors';
import { listKey } from '../../api/queryKeys';
import { PhoneField } from '../../components/PhoneField';
import { enumLabel, enumOptions } from '../../lib/enums';
import { formatDate } from '../../lib/format';
import { notifyError, notifySaved } from '../../lib/notify';
import { dialablePhone, formatPhone, isValidInternationalPhone } from '../../lib/phone';

export interface StaffMember {
  id: string;
  displayName: string;
  fullName?: string | null;
  status: string;
  language: string;
  contactPhone?: string | null;
  contactEmail?: string | null;
  hiredAt?: string | null;
  terminatedAt?: string | null;
  mustChangePin?: boolean;
  pinUpdatedAt?: string | null;
}

const MEMBERS_PATH = '/admin/v1/staff/members';

/** A read line, so the same facts sit in the same order whether you are reading or editing them. */
function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <Text size="xs" c="dimmed" tt="uppercase" fw={700} style={{ letterSpacing: '0.06em' }}>
        {label}
      </Text>
      <Text size="sm" mt={2}>
        {children}
      </Text>
    </div>
  );
}

function Missing({ children }: { children: ReactNode }) {
  return (
    <Text span size="sm" c="dimmed" fs="italic">
      {children}
    </Text>
  );
}

/**
 * Who this person is, changed on the page where you are already reading it.
 *
 * These fields used to be editable only through a dialog on the staff list, one click away from
 * the page that shows everything else about the same person — so the owner's own summary of it was
 * that employee management had been "split into too many areas". Worse than the walk between them
 * was that both places claimed the same fields: a phone number corrected in one and left stale in
 * the other, with no way to tell which the manager had used.
 *
 * There is now exactly one place each of these can be changed, and it is the one you are looking at.
 */
export function PersonDetailsPanel({ member }: { member: StaffMember }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);

  const form = useForm({
    initialValues: {
      displayName: member.displayName,
      fullName: member.fullName ?? '',
      language: member.language,
      hiredAt: member.hiredAt ?? '',
      contactPhone: member.contactPhone ?? '',
      contactEmail: member.contactEmail ?? '',
      status: member.status,
      terminatedAt: member.terminatedAt ?? '',
    },
    validate: {
      displayName: (v) => (v.trim() ? null : 'A name is required'),
      contactPhone: (v) =>
        v && !isValidInternationalPhone(v) ? 'Choose a country and enter a complete phone number' : null,
      /**
       * The field whose absence used to cost somebody their final wages. Payroll prorates the month
       * to this date and the register finds a leaver by it, so a person marked "no longer employed"
       * with the date blank did not merely get paid a full month — they dropped off the register
       * with no line and nothing to say a name was missing.
       */
      terminatedAt: (v, values) =>
        values.status === 'terminated' && !v
          ? 'Payroll needs the day they left to work out their final pay'
          : v && values.status === 'active'
            ? 'Set the status to no longer employed, or clear this date'
            : null,
    },
  });

  const save = useMutation({
    mutationFn: (values: typeof form.values) =>
      rawApi('PATCH', `${MEMBERS_PATH}/${member.id}`, {
        displayName: values.displayName.trim(),
        // Cleared boxes travel as an explicit null. Omitting them would leave the old value in
        // place behind a green "Saved" — a change the manager watched happen and that never did.
        fullName: values.fullName.trim() || null,
        language: values.language,
        hiredAt: values.hiredAt || null,
        contactPhone: values.contactPhone.trim() || null,
        contactEmail: values.contactEmail.trim() || null,
        status: values.status,
        terminatedAt: values.terminatedAt || null,
      }),
    onSuccess: (_data, values) => {
      void queryClient.invalidateQueries({ queryKey: listKey(MEMBERS_PATH) });
      setEditing(false);
      notifySaved(values.displayName.trim());
    },
    onError: (err: Error) => {
      const byField = fieldErrors(err);
      if (Object.keys(byField).length) form.setErrors(byField);
      notifyError(err, 'save these details');
    },
  });

  if (!editing) {
    return (
      <Stack gap="md">
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
          <Fact label="Name at the register">{member.displayName}</Fact>
          <Fact label="Full legal name">{member.fullName || <Missing>Not recorded</Missing>}</Fact>
          <Fact label="Started">
            {member.hiredAt ? formatDate(member.hiredAt) : <Missing>Not recorded</Missing>}
          </Fact>
          <Fact label="Phone">
            {member.contactPhone ? (
              // Callable, not just readable: the reason a manager opens somebody's page on a
              // Saturday is to ask them to cover a shift.
              <Anchor href={`tel:${dialablePhone(member.contactPhone)}`}>
                {formatPhone(member.contactPhone)}
              </Anchor>
            ) : (
              <Missing>Not recorded</Missing>
            )}
          </Fact>
          <Fact label="Email">
            {member.contactEmail ? (
              <Anchor href={`mailto:${member.contactEmail}`}>{member.contactEmail}</Anchor>
            ) : (
              <Missing>Not recorded</Missing>
            )}
          </Fact>
          <Fact label="Language">{enumLabel('language', member.language)}</Fact>
          <Fact label="Employment">{enumLabel('staffStatus', member.status)}</Fact>
          {member.terminatedAt && <Fact label="Last day worked">{formatDate(member.terminatedAt)}</Fact>}
        </SimpleGrid>
        <Group justify="flex-end">
          <Button size="sm" variant="light" onClick={() => setEditing(true)}>
            Change details
          </Button>
        </Group>
      </Stack>
    );
  }

  return (
    <form onSubmit={form.onSubmit((values) => save.mutate(values))}>
      <Stack gap="md">
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <TextInput
            label="Name at the register"
            description="How they appear on the register and on rotas"
            required
            {...form.getInputProps('displayName')}
          />
          <TextInput label="Full legal name" {...form.getInputProps('fullName')} />
          <DateInput
            label="Start date"
            valueFormat="D MMM YYYY"
            placeholder="Pick a date"
            clearable
            popoverProps={{ withinPortal: true }}
            value={form.values.hiredAt || null}
            onChange={(v) => form.setFieldValue('hiredAt', v ?? '')}
            error={form.errors.hiredAt}
          />
          {/*
            NOT "and printed tickets". A factura prints the words the tax office reads and nothing
            else decides them (domains/fiscal/pdf.ts), so the printed half of this sentence could
            never come true however much of the app was built — and a manager who read it would set
            a bartender to Spanish and expect the paper to follow.

            It says WHEN, because that is the half an owner has to be able to predict. The register
            switches at sign-in and switches back when they sign out; the flag in the corner of the
            register is the terminal's own language and this overrides it only while they are signed in.
            Until the sign-in read was built this field was read by nothing at all, so the sentence
            described a behavior the software did not have.
          */}
          <Select
            label="Language"
            description="The language the register switches to when they sign in"
            data={enumOptions('language')}
            comboboxProps={{ withinPortal: true }}
            {...form.getInputProps('language')}
          />
          <PhoneField
            label="Phone"
            value={form.values.contactPhone}
            onChange={(value) => form.setFieldValue('contactPhone', value)}
            error={form.errors.contactPhone}
            name="contactPhone"
          />
          <TextInput label="Email" {...form.getInputProps('contactEmail')} />
          {/*
            This said "ending employment switches their PIN off on every register at its next sync",
            which is a lock, described in the present tense, that nothing in the system performs:
            the save writes a status and touches neither the PIN nor any device. A manager who reads
            it walks away believing a dismissed bartender has been shut out, and stops there —
            which is the one belief on this screen that could cost real money on a Saturday night.
            What replaces it promises no mechanism and says the part he has to act on himself.
          */}
          <Select
            label="Employment"
            description="A record of whether they still work here. It does not take their PIN away on its own."
            data={enumOptions('staffStatus')}
            comboboxProps={{ withinPortal: true }}
            {...form.getInputProps('status')}
          />
          <DateInput
            label="Last day worked"
            description="Their final pay is worked out up to this day"
            valueFormat="D MMM YYYY"
            placeholder="Leave empty while they still work here"
            clearable
            popoverProps={{ withinPortal: true }}
            value={form.values.terminatedAt || null}
            onChange={(v) => form.setFieldValue('terminatedAt', v ?? '')}
            error={form.errors.terminatedAt}
          />
        </SimpleGrid>
        <Group justify="flex-end">
          <Button
            variant="default"
            onClick={() => {
              form.reset();
              setEditing(false);
            }}
            disabled={save.isPending}
          >
            Cancel
          </Button>
          <Button type="submit" loading={save.isPending}>
            Save details
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
